var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { krBaseCSS } from '../../style/base.js';
/**
 * A checkbox component that works with native browser forms.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * @element kr-checkbox
 * @fires change - Fired when the checked state changes
 */
export class KRCheckbox extends LitElement {
    constructor() {
        super();
        // The checked state the user last set. While dirty, an external `checked` set is reverted to this
        // (see willUpdate) so a parent re-binding from the server can't overwrite an unsaved toggle.
        this._dirtyChecked = false;
        /**
         * The checkbox label text
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * The value submitted when checked
         */
        this.value = 'on';
        /**
         * Whether the checkbox is checked
         */
        this.checked = false;
        /**
         * Whether the checkbox is disabled
         */
        this.disabled = false;
        /**
         * Whether the checkbox is required
         */
        this.required = false;
        /**
         * Helper text shown below the checkbox
         */
        this.hint = '';
        /**
         * Whether the user has toggled this field since it was last loaded or marked pristine. Set on
         * toggle, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's toggle is in flight. Set by markSaving(),
         * cleared by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._syncFormValue();
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved toggle, an external `checked` set (e.g. a parent re-binding from
        // a server response that predates the toggle) would overwrite it. Revert to the user's state.
        // The field accepts external values again once saved (markSaving clears dirty) or reset.
        if (changedProperties.has('checked') && this.dirty && this.checked !== this._dirtyChecked && saveMode === 'autosave') {
            this.checked = this._dirtyChecked;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('checked') || changedProperties.has('value')) {
            this._syncFormValue();
        }
        if (changedProperties.has('required') || changedProperties.has('checked')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.checked = false;
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's toggle starts. Clearing dirty here means
    // a further toggle during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user toggled mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the toggle.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyChecked = this.checked;
    }
    formStateRestoreCallback(state) {
        this.checked = state === this.value;
    }
    focus() {
        this.shadowRoot?.querySelector('.checkbox')?.focus();
    }
    blur() {
        this.shadowRoot?.querySelector('.checkbox')?.blur();
    }
    _syncFormValue() {
        if (this.checked) {
            this._internals.setFormValue(this.value);
        }
        else {
            this._internals.setFormValue('');
        }
    }
    _updateValidity() {
        if (this.required && !this.checked) {
            this._internals.setValidity({ valueMissing: true }, 'This field is required');
        }
        else {
            this._internals.setValidity({});
        }
    }
    _handleClick(e) {
        if (this.disabled) {
            return;
        }
        this.checked = !this.checked;
        this._touched = true;
        this.dirty = true;
        this._dirtyChecked = this.checked;
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    _handleKeyDown(e) {
        if (this.disabled) {
            return;
        }
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            this._handleClick(e);
        }
    }
    render() {
        return html `
      <div class="wrapper">
        <div
          class=${classMap({
            'checkbox': true,
            'checkbox--disabled': this.disabled,
        })}
          role="checkbox"
          aria-checked=${this.checked}
          aria-disabled=${this.disabled}
          aria-label=${this.label}
          tabindex=${this.disabled ? -1 : 0}
          @click=${this._handleClick}
          @keydown=${this._handleKeyDown}
        >
          <div
            class=${classMap({
            'checkbox__box': true,
            'checkbox__box--checked': this.checked,
            'checkbox__box--invalid': this._touched && this.required && !this.checked,
        })}
          >
            <svg
              class=${classMap({
            'checkbox__check': true,
            'checkbox__check--hidden': !this.checked,
        })}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          ${this.label
            ? html `
              <span class="checkbox__label">
                ${this.label}
                ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : nothing}
              </span>
            `
            : nothing}
        </div>
        ${this._touched && this.required && !this.checked
            ? html `<div class="validation-message">This field is required</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : nothing}
      </div>
    `;
    }
}
KRCheckbox.styles = [krBaseCSS, css `
    :host {
      display: inline-block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .checkbox {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }

    .checkbox--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .checkbox__box {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      border: 2px solid var(--kr-checkbox-border-color, #0000003d);
      border-radius: 4px;
      background-color: var(--kr-checkbox-bg, #fff);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
    }

    .checkbox:focus-visible .checkbox__box {
      outline: none;
      box-shadow: 0 0 0 3px rgba(22, 48, 82, 0.1);
    }

    .checkbox__box--checked {
      border-color: var(--kr-checkbox-checked-border-color, var(--kr-primary));
      background-color: var(--kr-checkbox-checked-bg, var(--kr-primary));
    }

    .checkbox__box--invalid {
      border-color: var(--kr-checkbox-error-border-color, #ef4444);
    }

    .checkbox__check {
      width: 12px;
      height: 12px;
      color: var(--kr-checkbox-check-color, #fff);
    }

    .checkbox__check--hidden {
      visibility: hidden;
    }

    .checkbox__label {
      font-size: 14px;
      color: var(--kr-checkbox-label-color, #000);
      user-select: none;
    }

    .required {
      color: var(--kr-checkbox-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-checkbox-helper-color, #6b7280);
      margin-left: 26px;
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-checkbox-error-color, #ef4444);
      margin-left: 26px;
    }
  `];
KRCheckbox.formAssociated = true;
__decorate([
    property({ type: String })
], KRCheckbox.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRCheckbox.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRCheckbox.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
], KRCheckbox.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean })
], KRCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRCheckbox.prototype, "required", void 0);
__decorate([
    property({ type: String })
], KRCheckbox.prototype, "hint", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRCheckbox.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRCheckbox.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRCheckbox.prototype, "saving", void 0);
__decorate([
    state()
], KRCheckbox.prototype, "_touched", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-checkbox')) {
    customElements.define('kr-checkbox', KRCheckbox);
}
//# sourceMappingURL=checkbox.js.map